import * as React from 'react';
import { Disclosure } from '@headlessui/react';
import { TOCItem } from 'constants/blog';
import classNames from 'styles/utils/classNames';

interface Props {
  items: TOCItem[];
  collapsible?: boolean;
}

export default function TableOfContents({ items, collapsible = false }: Props) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const TOCList = () => (
    <nav>
      <ul className="space-y-2">
        {items.map((item) => (
          <li
            key={item.id}
            className={classNames(item.level === 3 ? 'ml-4' : '')}
          >
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className="typography-product-caption block text-color-text-lightmode-secondary hover:text-color-text-lightmode-primary dark:text-color-text-darkmode-secondary dark:hover:text-color-text-darkmode-primary"
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );

  if (!items || items.length === 0) {
    return null;
  }

  // Mobile collapsible version
  if (collapsible) {
    return (
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div className="rounded-lg border border-color-border-input-lightmode p-4 dark:border-color-border-input-darkmode">
            <Disclosure.Button className="flex w-full items-center justify-between">
              <span className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Table of Contents
              </span>
              <svg
                className={classNames(
                  'h-5 w-5 text-color-text-lightmode-secondary transition-transform dark:text-color-text-darkmode-secondary',
                  open ? 'rotate-180' : ''
                )}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Disclosure.Button>
            <Disclosure.Panel className="mt-4">
              <TOCList />
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  }

  // Desktop sticky sidebar version
  return (
    <div className="sticky top-20">
      <div className="max-h-[calc(100vh-6rem)] overflow-y-auto rounded-lg border border-color-border-input-lightmode dark:border-color-border-input-darkmode">
        <div className="p-4">
          <h3 className="typography-product-subheading mb-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Table of Contents
          </h3>
          <TOCList />
        </div>
      </div>
    </div>
  );
}
